<script lang="ts">
    // upload de arquivo (imagem ou qualquer outro tipo) direto pro S3 via link pré-assinado — o
    // conteúdo do arquivo nunca trafega pro nosso backend em base64, só o next é o resultado final
    // (a URL) que fica em "value". type "file"/"image" não existe aqui de propósito: quem decide se
    // mostra miniatura ou ícone genérico é a própria extensão do arquivo já enviado, não uma prop.
    import { createEventDispatcher } from 'svelte'
    import Fa from 'svelte-fa'
    import { faFileUpload, faFile, faTimes } from '@fortawesome/free-solid-svg-icons'

    // reaproveita a prop "options" que o Zinput já tem (normalmente usada por select/multiselect)
    // pra restringir os arquivos aceitos — cada "opção" aqui é um padrão aceito pelo atributo nativo
    // "accept" do <input type="file"> (ex.: {value:'.pfx'} ou {value:'image/*'}), não um valor
    // escolhível. sem nenhuma opção informada, aceita qualquer arquivo.
    export let value: string | string[] = null
    export let options: { value: any; label?: string }[] = []
    export let multi = false
    export let disabled = false
    // recebido só por consistência com os demais tipos (o marcador visual "*" é desenhado pelo
    // zinput.svelte, fora daqui) — não vai pro atributo "required" do <input type="file"> nativo de
    // propósito: como o FileList dele é limpo a cada seleção (pra permitir escolher o mesmo arquivo
    // de novo), o required nativo ficaria sempre "não preenchido" mesmo depois de um upload
    // concluído. export const (em vez de export let) porque o componente não usa esse valor.
    export const required: boolean = false
    // por padrão o arquivo vai para o bucket protegido, e a URL gravada só abre com token — é o que
    // a maioria das telas internas quer. presign={false} sobe para o bucket público e devolve uma URL
    // absoluta, necessária quando quem exibe o arquivo é o app do cliente final (sem login).
    export let presign: boolean = true
    export let labeled = false
    export let htmlInput = null
    // reduz a imagem para caber nestes limites (mantendo a proporção) antes de subir. zero desliga,
    // que é o padrão — arquivo não-imagem e formatos que não devem passar por canvas (svg, gif)
    // seguem intactos de qualquer forma. o navegador decodifica a imagem no tamanho do arquivo, não
    // no tamanho em que ela é exibida, então o que sobe aqui é o custo que todo mundo paga depois.
    export let maxWidth: number = 0
    export let maxHeight: number = 0

    const dispatch = createEventDispatcher()

    // pasta fixa no S3 pra todo upload feito através do zinput — não é uma decisão por instância
    // (como accept), é só organização interna do storage. vale só para o upload pré-assinado: no
    // caminho público quem define a pasta é o events-api.
    const FOLDER_NAME = 'zinput-upload'
    const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'heic', 'svg']

    let nativeInput: HTMLInputElement = null
    let uploading = false

    // a URL que volta do upload não é o nome original do arquivo — é algo como
    // "/files?file=<pasta>%2F<uuid>.png" (a chave gerada no S3, não o nome que o usuário escolheu).
    // guarda o nome original aqui (só localmente, nunca some pra "value") pra exibir algo legível
    // no chip; se a URL já vier de fora (valor pré-existente vindo do backend, sem passar por um
    // upload nesta sessão), cai no fallback de extrair da própria URL.
    let displayNames: Record<string, string> = {}

    // a "key" real do arquivo (nome+extensão) pode estar no path da URL OU num parâmetro de query
    // "file" (é assim que o endpoint de download deste app funciona: /files?file=<chave-no-s3>) —
    // sem isso, tanto o ícone de imagem quanto o nome exibido ficavam errados (mostrava só "files",
    // o nome literal do path, ignorando a extensão real que está na query).
    function extractFileKey(url: string): string {
        try {
            const parsed = new URL(url, window.location.origin)
            const fileParam = parsed.searchParams.get('file')
            if (fileParam) return decodeURIComponent(fileParam)
            return decodeURIComponent(parsed.pathname)
        } catch (error) {
            return url
        }
    }

    $: accept = options.map((o) => o.value).join(',')
    $: files = multi ? (Array.isArray(value) ? value : []) : value ? [value as string] : []

    function openPicker() {
        if (disabled || uploading) return
        nativeInput.click()
    }

    function onTriggerKeydown(ev) {
        if (ev.key === 'Enter' || ev.key === ' ') {
            ev.preventDefault()
            openPicker()
        }
    }

    async function onFilesChanged() {
        const picked = Array.from(nativeInput.files || [])
        // zera o input nativo assim que lê a seleção, pra permitir escolher o mesmo arquivo de novo
        // depois (o browser não dispara "change" se o valor não mudar)
        nativeInput.value = ''
        if (!picked.length) return
        uploading = true
        try {
            for (const pickedFile of picked) {
                const url = await uploadOne(pickedFile)
                value = multi ? [...(Array.isArray(value) ? value : []), url] : url
                dispatch('change', { value })
            }
        } catch (error) {
            window.error(error)
        } finally {
            uploading = false
        }
    }

    async function uploadOne(pickedFile: File): Promise<string> {
        let extension = window.api.events.files.getExtension(pickedFile.name) || ''
        let base64 = await readAsBase64(pickedFile)
        if (extension.toLowerCase() === 'heic') {
            base64 = await window.api.utils.images.convertHeicBase64ToJpg(base64)
            extension = 'jpg'
        }
        // depois do HEIC (que já normalizou para jpg) e antes de subir
        if (maxWidth && maxHeight && IMAGE_EXTENSIONS.includes(extension.toLowerCase())) {
            base64 = await window.api.utils.images.resizeBase64(base64, maxWidth, maxHeight)
        }
        // com presign, o navegador envia o conteúdo direto pro S3 (PUT binário) e o base64 nunca chega
        // ao nosso backend; sem presign, o base64 vai para o events-api, que grava no bucket público.
        // nos dois casos o base64 não persiste em "value", que só guarda a URL final.
        const result = presign
            ? await window.api.events.files.uploadToSignedLink({ content: base64, extension, folderName: FOLDER_NAME })
            : await window.api.events.files.upload({ content: base64, folderName: FOLDER_NAME })
        displayNames[result.url] = pickedFile.name
        displayNames = displayNames
        return result.url
    }

    function readAsBase64(pickedFile: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = () => resolve(reader.result as string)
            reader.onerror = () => reject(reader.error)
            reader.readAsDataURL(pickedFile)
        })
    }

    function isImageUrl(url: string): boolean {
        const key = extractFileKey(url).toLowerCase()
        const ext = key.substring(key.lastIndexOf('.') + 1)
        return IMAGE_EXTENSIONS.includes(ext)
    }

    function fileNameFromUrl(url: string): string {
        if (displayNames[url]) return displayNames[url]
        const key = extractFileKey(url)
        return key.substring(key.lastIndexOf('/') + 1) || url
    }

    function removeFile(ev, url: string) {
        ev.stopPropagation()
        if (disabled) return
        value = multi ? (Array.isArray(value) ? value : []).filter((el) => el !== url) : null
        dispatch('change', { value })
    }

    function onFocus(ev) {
        dispatch('focus', ev)
    }
    function onBlur(ev) {
        dispatch('blur', ev)
    }
</script>

<div
    class="zinput-file zinput_input zinput_input_labeled-{labeled}"
    class:zinput-file_uploading={uploading}
    role="button"
    tabindex={disabled ? -1 : 0}
    aria-disabled={disabled}
    on:click={openPicker}
    on:keydown={onTriggerKeydown}
    on:focus={onFocus}
    on:blur={onBlur}
    bind:this={htmlInput}
>
    <!-- input nativo escondido: quem dispara a seleção é sempre o clique/teclado no trigger acima,
         igual ao padrão já usado no ImagePic e no zinput-expander -->
    <input
        bind:this={nativeInput}
        type="file"
        {accept}
        {disabled}
        multiple={multi}
        tabindex="-1"
        class="zinput-file_native"
        on:change={onFilesChanged}
    />

    {#if files.length === 0}
        <!-- quando tem label, o próprio label (fora daqui, desenhado por zinput.svelte) já ocupa essa
             posição "grande" de placeholder quando o campo está vazio (mesma convenção do
             zinput-multiselect, que também não desenha nada próprio nesse estado) — mostrar um hint
             nosso aqui em cima colidiria visualmente com o texto do label. sem label, não há nada
             mais indicando a função do campo, então mantém o ícone + texto. -->
        {#if !labeled}
            <div class="zinput-file_empty">
                <Fa icon={faFileUpload} />
                <span>Selecionar arquivo{multi ? 's' : ''}</span>
            </div>
        {/if}
    {:else}
        <div class="zinput-file_list">
            {#each files as fileUrl (fileUrl)}
                <div class="zinput-file_chip">
                    {#if isImageUrl(fileUrl)}
                        <img class="zinput-file_thumb" src={fileUrl} alt="" />
                    {:else}
                        <Fa icon={faFile} />
                    {/if}
                    <span class="zinput-file_name" title={fileNameFromUrl(fileUrl)}>{fileNameFromUrl(fileUrl)}</span>
                    {#if !disabled}
                        <span class="zinput-file_remove" role="button" tabindex="0" title="Remover" on:click={(ev) => removeFile(ev, fileUrl)}>
                            <Fa icon={faTimes} />
                        </span>
                    {/if}
                </div>
            {/each}
        </div>
    {/if}

    {#if uploading}
        <div class="zinput-file_spinner"></div>
    {/if}
</div>

<style>
    .zinput-file {
        display: flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
        min-width: 0;
        overflow-x: auto;
        position: relative;
    }

    .zinput-file[aria-disabled='true'] {
        cursor: not-allowed;
    }

    .zinput-file_uploading {
        pointer-events: none;
        opacity: 0.7;
    }

    .zinput-file_native {
        position: absolute;
        top: -30000px;
        left: -30000px;
        width: 1px;
        height: 1px;
        opacity: 0;
    }

    .zinput-file_empty {
        display: flex;
        align-items: center;
        gap: 6px;
        opacity: 0.6;
        font-size: 14px;
        white-space: nowrap;
    }

    .zinput-file_list {
        display: flex;
        align-items: center;
        gap: 6px;
        flex-wrap: wrap;
        min-width: 0;
    }

    .zinput-file_chip {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background-color: #ffffff15;
        border-radius: 6px;
        padding: 3px 6px;
        font-size: 13px;
        max-width: 220px;
    }

    .zinput-file_thumb {
        width: 20px;
        height: 20px;
        object-fit: cover;
        border-radius: 3px;
        flex-shrink: 0;
    }

    .zinput-file_name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .zinput-file_remove {
        opacity: 0.6;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        font-size: 11px;
    }

    .zinput-file_remove:hover {
        opacity: 1;
    }

    .zinput-file_spinner {
        flex-shrink: 0;
        box-sizing: border-box;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        border: 3px solid #99999999;
        border-top-color: #fff;
        animation: zinput-file-spin 0.6s linear infinite;
    }

    @keyframes zinput-file-spin {
        to {
            transform: rotate(360deg);
        }
    }
</style>
